import type { SelectedAsset as BaseSelectedAsset } from '../features/types';
import type { PopupPoint } from '@Components/Popup/types';

// ---------------------------------------------------------------------------
// Basemap
// ---------------------------------------------------------------------------

/** A selectable basemap entry, e.g. for a gallery or dropdown. */
export interface BasemapOption {
	id: EsriBasemapId;
	label: string;
	thumbnailUrl?: string;
}

/** ArcGIS basemap styles based on the REST API basemap styles.
 *  @see https://developers.arcgis.com/rest/basemap-styles/ */
export enum EsriBasemapId {
	Community = 'arcgis/community',
	DarkGray = 'arcgis/dark-gray',
	Imagery = 'arcgis/imagery',
	ImageryWithoutLabels = 'arcgis/imagery/standard',
	LabelsOnly_DarkGray = 'arcgis/dark-gray/labels',
	LabelsOnly_Imagery = 'arcgis/imagery/labels',
	LabelsOnly_LightGray = 'arcgis/light-gray/labels',
	LightGray = 'arcgis/light-gray',
	LightGrayLabels = 'arcgis/light-gray/labels',
	Navigation = 'arcgis/navigation',
	NavigationNight = 'arcgis/navigation-night',
	Oceans = 'arcgis/oceans',
	Streets = 'arcgis/streets',
	StreetsNight = 'arcgis/streets-night',
	StreetsWithRelief = 'arcgis/streets-relief',
	Terrain = 'arcgis/terrain',
	ChartedTerritory = 'arcgis/charted-territory',
	Topographic = 'arcgis/topographic',
	OpenDarkGray = 'open/dark-gray',
	OpenHybrid = 'open/hybrid',
	OpenStreets = 'open/streets',
	OpenStreetsRelief = 'open/streets-relief',
}

// ---------------------------------------------------------------------------
// Map initialisation
// ---------------------------------------------------------------------------

/** Config passed to {@link useEsriMap} to initialise the MapView. */
export interface EsriMapConfig {
	/** Initial map center as `[longitude, latitude]`. */
	center?: [number, number];
	/** Initial zoom level. */
	zoom?: number;
	/** Starting basemap. Defaults to `EsriBasemapId.OpenStreets`. */
	basemap?: EsriBasemapId;
	/** Background basemap style using ArcGIS REST API basemap styles. */
	background?: EsriBasemapId;
	/** ArcGIS platform API key. */
	apiKey?: string;

	maxZoom?: number;
	minZoom?: number;
}

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------

/** Config for adding a FeatureLayer to the map. */
export interface LayerConfig {
	id: string;
	url: string;
	title?: string;
	visible?: boolean;
	definitionExpression?: string;
	opacity?: number;
}

// ---------------------------------------------------------------------------
// Theming / renderers
// ---------------------------------------------------------------------------

/** Categorical color mapping applied via `UniqueValueRenderer`. */
export interface AttributeColorConfig {
	field: string;
	values: Array<{ value: string | number; color: string }>;
}

/** Numeric break-based color mapping applied via `ClassBreaksRenderer`. */
export interface ColorRangeConfig {
	field: string;
	breaks: Array<{ min: number; max: number; color: string; label?: string }>;
}

/** Config for a `HeatmapRenderer`. */
export interface HeatmapConfig {
	field?: string;
	colorStops?: Array<{ ratio: number; color: string }>;
	radius?: number;
	maxDensity?: number;
}

/** Per-layer style overrides within a theme.
 *
 *  @remarks ArcGIS scale values are the inverse of zoom levels:
 *  `scale ≈ 591657527.591555 / 2^zoom`. Use a conversion helper when mapping
 *  from zoom to `minScale` / `maxScale`. */
export interface LayerStyleConfig {
	fixedColor?: string;
	attributeColor?: AttributeColorConfig;
	colorByRange?: ColorRangeConfig;
	heatmap?: HeatmapConfig;
	strokeWidth?: number;
	/** ArcGIS map scale at which the layer becomes visible (lower bound). */
	minScale?: number;
	/** ArcGIS map scale at which the layer is hidden (upper bound). */
	maxScale?: number;
	blendMode?: string;
}

/** Input shape for creating or updating a theme. */
export interface ThemeConfig {
	name: string;
	/** Keyed by layer ID. */
	layers: Record<string, LayerStyleConfig>;
	isDefault?: boolean;
}

/** A persisted theme returned from the theme API. */
export interface Theme {
	id: string;
	name: string;
	/** Keyed by layer ID. */
	layers: Record<string, LayerStyleConfig>;
	isDefault: boolean;
}

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/** Re-export of the shared asset selection type. */
export type SelectedAsset = BaseSelectedAsset;

// ---------------------------------------------------------------------------
// Hover popup parity contract
// ---------------------------------------------------------------------------

/**
 * Canonical hover card fields expected by host applications.
 *
 * The ESRI hover implementation should always populate these canonical keys.
 * If source data uses alternate names (for example install_date), normalize
 * them before emitting popup payloads.
 */
export interface HoverPopupSummaryFields {
	/** Asset identifier shown on the hover card. */
	assetId?: string;
	/** Asset type shown on the hover card. */
	assetType?: string;
	/** Installation date shown on the hover card. */
	installDate?: string | number | Date | null;
}

/**
 * Feature shape passed to the hover popup component.
 *
 * This extends selected asset metadata with canonical summary fields and
 * optional alias fields for compatibility with existing datasets.
 */
export type HoverPopupFeature = SelectedAsset &
	HoverPopupSummaryFields & {
		/** Common legacy alias in tile payloads. */
		install_date?: string | number | Date | null;
		/** Alternate backend naming seen in some services. */
		installationDate?: string | number | Date | null;
		[key: string]: unknown;
	};

/**
 * Payload consumed by Popup for asset hover behavior.
 *
 * Keep this aligned with the existing ReactMap hover popup contract:
 * - point: screen position where popup should anchor
 * - features: ordered feature list; first item drives primary card content
 */
export interface HoverPopupPayload {
	point?: PopupPoint;
	features?: HoverPopupFeature[];
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

/** A single result returned from the search module. */
export interface SearchResult {
	id: string;
	name: string;
	/** `arcgis/core/geometry` Geometry at runtime. */
	geometry?: unknown;
	attributes?: Record<string, unknown>;
	sourceId?: string;
}

/** Configuration for a custom search source used by the Search widget. */
export interface SearchSource {
	id: string;
	name: string;
	/** REST endpoint URL, if not backed by a FeatureLayer. */
	url?: string;
	/** `arcgis/core/layers/FeatureLayer` at runtime. */
	layer?: unknown;
	searchFields?: string[];
	displayField?: string;
	outFields?: string[];
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

/** A single row in the asset grid. */
export interface AssetRecord {
	id: string;
	[key: string]: unknown;
}

/** Spatial and attribute filter applied to the asset grid query. */
export interface GridFilter {
	systemType?: string;
	assetType?: string;
	/** `Polygon` (`@arcgis/core/geometry/Polygon`) at runtime. */
	polygon?: unknown | null;
}

// ---------------------------------------------------------------------------
// Property pane data
// ---------------------------------------------------------------------------

/** Full property bag loaded for a selected asset. */
export interface AssetProperties {
	id: string;
	[key: string]: unknown;
}

/** Inspection records associated with an asset. */
export interface InspectionData {
	assetId: string;
	inspections: unknown[];
}

/** Risk model output for an asset. */
export interface RiskData {
	assetId: string;
	score?: number;
	[key: string]: unknown;
}

/** Rehabilitation recommendation for an asset. */
export interface RehabData {
	assetId: string;
	recommendation?: string;
	[key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Mini map
// ---------------------------------------------------------------------------

/** Config for creating a mini-map instance. */
export interface MiniMapConfig {
	basemap?: EsriBasemapId;
	zoom?: number;
	/** Whether the mini map responds to user interaction. Defaults to `false`. */
	interactive?: boolean;
}
